import React, { Fragment, useEffect, useState } from 'react';
import Box from "@mui/material/Box";
import { AnalysisSummaryViewerProps } from './AnalysisSummaryViewer.props';
import './AnalysisSummaryViewer.styles.css'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorIcon from "@mui/icons-material/Error";
import LaunchIcon from '@mui/icons-material/Launch';
import PersonIcon from '@mui/icons-material/Person';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import BuildIcon from '@mui/icons-material/Build';
import PlaceIcon from '@mui/icons-material/Place';
import DnsIcon from '@mui/icons-material/Dns';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Image } from "../../atoms/Image";
import { Images } from '../../../configs/images';
import { pieArcClasses, pieArcLabelClasses, PieChart } from '@mui/x-charts/PieChart';
import WarningIcon from "@mui/icons-material/Warning";
import { NoThreatDetectedCard } from '../NoThreatDetectedCard'
import { ThreatScoreTooltip } from "../../atoms/ThreatScoreTooltip";
import { AccordionDetails, styled, Tooltip, tooltipClasses, TooltipProps } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import Button from "@mui/material/Button";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import { ANALYSIS_TYPES } from "../../../constants/contants";
import { classifyHeaderRuleCategory, classifyThreatScore, ThreatScoreClassification } from "../../../utils/threatScore";

const scanResultKeys = {
    [ANALYSIS_TYPES.DNS]: 'dns',
    [ANALYSIS_TYPES.IPS]: 'ip',
    [ANALYSIS_TYPES.URLS]: 'url',
    [ANALYSIS_TYPES.SENDERS]: 'sender',
    [ANALYSIS_TYPES.EMAILS]: 'email',
    [ANALYSIS_TYPES.ATTACHMENTS]: 'attachment',
    [ANALYSIS_TYPES.CONTENT_OVERALL]: '',
    [ANALYSIS_TYPES.HEADER_ANALYSIS]: '',
}

const RULE_ACTION_PRIORITIES = new Set(['High', 'Medium', 'Low', 'Unknown']);
const RULE_ACTION_CATEGORIES = new Set(['Threat', 'Clean', 'Unknown']);

type HeaderRuleOverview = {
    classification: ThreatScoreClassification;
    priority?: string;
    category?: string;
};

type HeaderRuleCardData = {
    matchedRules: Array<{
        id: string;
        name: string;
        score: number;
        matchedChecks: string[];
    }>;
};

const HEADER_CHECK_PROTOCOLS: Record<string, string> = {
    spf_result: 'SPF',
    spf_alignment: 'SPF',
    dkim_result: 'DKIM',
    dkim_alignment: 'DKIM',
    dmarc_result: 'DMARC',
    dmarc_compliance: 'DMARC',
    arc_result: 'ARC',
};

const getMatchedCheckLabels = (matchedChecks: unknown): string[] => {
    if (!Array.isArray(matchedChecks)) return [];

    const seen = new Set<string>();
    return matchedChecks.reduce<string[]>((labels, check) => {
        if (!check || typeof check !== 'object') return labels;
        const checkKey = (check as {check?: unknown}).check;
        const protocol = typeof checkKey === 'string' ? HEADER_CHECK_PROTOCOLS[checkKey] : undefined;
        if (!protocol || seen.has(protocol)) return labels;
        seen.add(protocol);
        labels.push(protocol);
        return labels;
    }, []);
};

const getHeaderRuleCardData = (headerAnalysis: unknown): HeaderRuleCardData | undefined => {
    if (!Array.isArray(headerAnalysis)) return undefined;

    const summary = headerAnalysis.find((item: unknown) => Boolean(item)
        && typeof item === 'object'
        && (item as {type?: unknown}).type === 'header_analysis_summary') as Record<string, any> | undefined;
    const selectedRule = summary?.selected_rule;
    if (!summary || summary.skipped === true || !selectedRule || typeof selectedRule !== 'object'
        || Array.isArray(selectedRule)) {
        return undefined;
    }

    const selectedScore = Number(selectedRule.header_rule_score);
    if (!Number.isFinite(selectedScore)
        || typeof selectedRule.id !== 'string' || !selectedRule.id
        || typeof selectedRule.name !== 'string' || !selectedRule.name) {
        return undefined;
    }

    const matchedRules: HeaderRuleCardData['matchedRules'] = [{
        id: selectedRule.id,
        name: selectedRule.name,
        score: selectedScore,
        matchedChecks: getMatchedCheckLabels(selectedRule.matched_checks),
    }];

    if (Array.isArray(summary.matched_rules)) {
        summary.matched_rules.forEach((rule: any) => {
            const score = Number(rule?.header_rule_score);
            if (!rule || typeof rule.id !== 'string' || rule.id === selectedRule.id
                || typeof rule.name !== 'string' || !rule.name || !Number.isFinite(score)) {
                return;
            }
            matchedRules.push({
                id: rule.id,
                name: rule.name,
                score,
                matchedChecks: getMatchedCheckLabels(rule.matched_checks),
            });
        });
    }

    return {matchedRules};
};

/**
 * Overview must use the deterministic Header Threat Scoring winner, not the
 * numeric final score, when a rule was actually applied.  Historic scans and
 * malformed metadata intentionally fall back to the existing score ranges.
 */
export const getHeaderRuleOverview = (headerAnalysis: unknown): HeaderRuleOverview | undefined => {
    if (!Array.isArray(headerAnalysis)) return undefined;

    const summary = headerAnalysis.find((item: unknown) => {
        return Boolean(item) && typeof item === 'object'
            && (item as {type?: unknown}).type === 'header_analysis_summary';
    }) as {skipped?: unknown; selected_rule?: unknown} | undefined;
    if (!summary || summary.skipped === true || !summary.selected_rule
        || typeof summary.selected_rule !== 'object' || Array.isArray(summary.selected_rule)) {
        return undefined;
    }

    const rule = summary.selected_rule as {
        category?: unknown;
        actions?: {priority?: unknown; category?: unknown};
    };
    const classification = classifyHeaderRuleCategory(rule.category);
    if (!classification) return undefined;

    return {
        classification,
        priority: RULE_ACTION_PRIORITIES.has(String(rule.actions?.priority))
            ? String(rule.actions?.priority)
            : undefined,
        category: RULE_ACTION_CATEGORIES.has(String(rule.actions?.category))
            ? String(rule.actions?.category)
            : undefined,
    };
};

export const AnalysisSummaryViewer: React.FC<AnalysisSummaryViewerProps>= ({
    payLoad = {},
    handleViewScanResultDetail,
    setLoadingPage,
    analysisExpanded,
    setAnalysisExpanded,
}) => {
    const threatScoreData = payLoad
    const isNoData = Object.keys(threatScoreData).length === 0

    const [timer, setTimer] = useState<any>(null)

    useEffect(() => {
        setLoadingPage(isNoData)
    }, [threatScoreData])

    const priorityStyleDataForm:any = {
        "high": {
            style: {
                color: "#fff",
                backgroundColor: "#ED1C24"
            },
            text: "High",
            suggestionContent: "Emails with a high priority contain a potential threat and should be investigated and addressed immediately.",
        },
        "medium":  {
            style: {
                color: "#fff",
                backgroundColor: "#FCAB10"
            },
            text: "Medium",
            suggestionContent: "Emails with medium priority contain undetermined Threat Analysis results and should be investigated further."
        },
        "moderate":  {
            style: {
                color: "#fff",
                backgroundColor: "#FCAB10"
            },
            text: "Moderate",
            suggestionContent: "Emails with medium priority contain undetermined Threat Analysis results and should be investigated further."
        },
        "low": {
            style: {
                color: "#fff",
                backgroundColor: "#03A9F5"
            },
            text: "Low",
            suggestionContent: "Emails with low priority do not appear to contain any threat."
        },
        "unknown": {
            style: {
                color: "#fff",
                backgroundColor: "#909090"
            },
            text: "Unknown",
            suggestionContent: "Priority is yet to be determined, check back at a later time when the email has been analysed."
        }
    }

    const senderObjectNames:object = {
        "sender_email": "Sender email address",
        "sender_domain": "Sender domain",
        "sender_ip_v6": "IPV6",
        "sender_ip_v4": "IPV4",
    }

    const deliveryInformationKeys:string[] = ["header_spf", "header_dmarc", "header_dkim", "header_arc"]

    const pieChartStartPoint = -90
    let getArcLabel:any
    let pieChartEndPoint:number = 0

    let pieChartData:any = []
    let priorityStyleData:any = []
    let explainText:string = ""
    let messageIcon:React.ReactElement = <></>
    const headerRuleOverview = getHeaderRuleOverview(threatScoreData?.header_analysis);
    let summary_analysis_category = headerRuleOverview?.category || threatScoreData?.category
    let priorityKey = (headerRuleOverview?.priority || threatScoreData?.priority || 'unknown').toLowerCase()

    if (!isNoData){
        const totalThreatScore = threatScoreData.score
        const scoreClassification = headerRuleOverview?.classification || classifyThreatScore(totalThreatScore)
        // -1 is analysing state
        if (totalThreatScore === -1){
            getArcLabel = () => ("N/A")
        }else {
            getArcLabel = () => (totalThreatScore.toString())
            pieChartEndPoint = pieChartStartPoint + (1.8 * totalThreatScore)
        }

        pieChartData.push({
            label: scoreClassification.description,
            value: Math.max(0, totalThreatScore),
            color: scoreClassification.color
        })
        explainText = scoreClassification.explanation

        summary_analysis_category = headerRuleOverview?.category || threatScoreData?.category
        // These suggestions are generated from the normal score ranges. They
        // would contradict a rule-driven Overview, so retain the rule-level
        // priority guidance whenever Header Threat Scoring supplied the view.
        if (!headerRuleOverview) {
            threatScoreData.suggestions.forEach((item:any) => {
                if (item.message){
                    priorityStyleDataForm[priorityKey].suggestionContent = item.message
                    return
                }
            })
        }
        priorityStyleData.push(priorityStyleDataForm[priorityKey])
        messageIcon = scoreClassification.category === 'Undetermined' ? <RemoveCircleIcon style={{marginRight: '8px'}} htmlColor={'#FCAB10'}/> : <WarningIcon style={{marginRight: '8px'}} htmlColor={'#FCAB10'}/>
    }

    const ThreatAnalysisTooltipStyle = styled(({ className, ...props }: TooltipProps) => (
        <Tooltip className={'custom-tooltip'} {...props} classes={{ popper: className }} />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: '#ffffff',
            color: '#212121',
            alignItems: "center",
            maxWidth: "480px",
            borderRadius: "4px",
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
            fontSize: '14px',
            padding: '16px',
        },
    }));

    const bytesToSize = function(bytes:any, decimals = 2) {
        if (!+bytes) return '0 Bytes'

        const k = 1024
        const dm = decimals < 0 ? 0 : decimals
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

        const i = Math.floor(Math.log(bytes) / Math.log(k))

        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
    }

    const handleChangeEmailDetailActiveTab = (type:string) => {
        // 1 is index number of Scan Result tab in EmailDetails
        handleViewScanResultDetail(1)
        if (timer) {
            clearTimeout(timer)
            setTimer(null)
        }
        return new Promise(() => {
            const timers = setTimeout(async () => {
                const classNameElement = 'scan-result-'+type+'-box'
                const emailDetailElement = document.getElementById('email-detail')
                const scanResultBox = emailDetailElement?.getElementsByClassName(classNameElement)[0]

                if (scanResultBox) {
                    scanResultBox.scrollIntoView({ behavior: 'smooth', block: "center"})
                }
            }, 100)
            setTimer(timers)
        });
    }

    const handleEntitiesContentComponent = (type:string, dataList:any) => {
        const pluralizeForm = {
            ip: "IP is",
            ips: "IPs are",
            dns: "DNS is",
            dnss: "DNSs are",
            email: "Email address is",
            emails: "Email addresses are",
            url: "URL is",
            urls: "URLs are",
            attachment: "Attachment is",
            attachments: "Attachments are"
        }

        let threatList:any = []
        let undeterminedList:any = []
        const threatTag = "threat"
        const undeterminedTag = "undetermined"
        if (type === "url" || type === "attachment"){
            dataList.forEach((item:any) => {
                let content = ""
                if (type === "attachment"){
                    content = item.file_name + ' (' + bytesToSize(item.file_size) + ')'
                }else {
                    content = item.data
                }

                if (item.threat_category === "malicious"){
                    threatList.push(content)
                }else {
                    undeterminedList.push(content)
                }
            })
        }else {
            dataList.forEach((item:any) => {
                if (item.priority === "high"){
                    threatList.push(item.data)
                }else {
                    undeterminedList.push(item.data)
                }
            })
        }

        const threatMessage = <>{pluralizeForm[threatList.length === 1 ? type : type+"s"]} classified as {threatList.length === 1 ? "a " : ""}<strong>{threatTag}</strong>:</>
        const undeterminedMessage =  <>{pluralizeForm[undeterminedList.length === 1 ? type : type+"s"]} classified as {undeterminedList.length === 1 ? "a " : ""}<strong>{undeterminedTag}</strong>:</>

        return  <Box className={"entities-content"}>
            {
                threatList.length > 0 ? <Box className={"entities-attribute"} >
                    <WarningIcon style={{marginRight: '8px'}} htmlColor={'#ED1C24'}/>
                    {
                        <Box className={'entities-attribute-message'}>
                            {threatMessage}
                            {
                                threatList.map((item:string, idx:any) =>
                                    <Box sx={{marginLeft: "10px", wordBreak: "break-word", textOverflow: "ellipsis"}} key={idx}>. &nbsp; {item}</Box>
                                )
                            }
                        </Box>
                    }
                </Box> : <></>
            }
            {
                undeterminedList.length > 0 ? <Box className={"entities-attribute"} >
                    <RemoveCircleIcon style={{marginRight: '8px'}} htmlColor={'#FCAB10'}/>
                    {
                        <Box className={'entities-attribute-message'}>
                            {undeterminedMessage}
                            {
                                undeterminedList.map((item:string, idx:any) =>
                                    <Box sx={{marginLeft: "10px", wordBreak: "break-word", textOverflow: "ellipsis"}} key={idx}>. &nbsp; {item}</Box>
                                )
                            }
                        </Box>
                    }
                </Box> : <></>
            }
        </Box>
    }

    const handleReasonNote = () => {
        let count = 0
        if(!isNoData){
            const keyArr = ["senders", "ips", "dns", "emails", "urls", "attachments", "content_overall", "header_analysis"]
            keyArr.forEach((item: string) => {
                if (threatScoreData[item]?.length > 0) {
                    count += 1
                }
            })
        }

        const reasonNote = `reason${count !== 1 ? 's' : ''}`
        return (
          headerRuleOverview ?
            <Box sx={{ marginTop: "24px" }}>
                Your <strong>Threat Score Rules</strong> classified this email as <strong>
                {(priorityStyleData[0]?.text || threatScoreData.priority || 'Unknown').toLowerCase()} priority
                </strong>. Phish Focus detected the following indicators:
            </Box> : pieChartData[0]?.value !== 0 ?
            <Box sx={{ marginTop: "24px" }}>
                Phish Focus has classified this as
                a <strong>{(priorityStyleData[0]?.text || threatScoreData.priority || 'Unknown').toLowerCase()} priority</strong> for the following {reasonNote}:
            </Box> : <Box sx={{ marginTop: "24px" }}>
                Phish Focus has classified this as <strong>No Threat.</strong> However, minor issues were found in <strong>Scan
                Results</strong> and/or <strong>Header Analysis</strong>.<br/>Please review those tabs to determine if action is needed. Issues are listed below:
            </Box>
        )
    }

    const renderThreatScoreRuleCard = (cardData: HeaderRuleCardData) => (
        <Box className="header-rule-card" data-testid="header-rule-card">
            <strong className="header-rule-card-title">
                Matched Threat Score Rules ({cardData.matchedRules.length})
            </strong>
            {cardData.matchedRules.map((rule, index) => <Box className="header-rule-item" key={rule.id}>
                <span className="header-rule-item-number">{index + 1}.</span>
                <Box className="header-rule-item-details">
                    <Box className="header-rule-item-heading">
                        <strong>{rule.name}</strong>
                        <span className="header-rule-item-separator" aria-hidden="true" />
                        <span>Threat score: <strong>{rule.score}</strong></span>
                    </Box>
                    <span>
                        Failed authentication checks: <strong>{rule.matchedChecks.length > 0
                            ? rule.matchedChecks.join(', ')
                            : 'None'}</strong>
                    </span>
                </Box>
            </Box>)}
        </Box>
    );

    const getAnalysis = (analysisType: ANALYSIS_TYPES) => {
        const scanResultKey = scanResultKeys[analysisType];
        const headerRuleCard = analysisType === ANALYSIS_TYPES.HEADER_ANALYSIS
            ? getHeaderRuleCardData(threatScoreData.header_analysis)
            : undefined;
        if (analysisType === ANALYSIS_TYPES.HEADER_ANALYSIS && !headerRuleCard) {
            return null;
        }
        let title = '';
        let HeaderIcon = PersonIcon;
        switch (analysisType) {
            case ANALYSIS_TYPES.SENDERS:
                title = 'Sender Analysis';
                HeaderIcon = PersonIcon;
                break;
            case ANALYSIS_TYPES.IPS:
                title = 'IP Analysis';
                HeaderIcon = PlaceIcon;
                break;
            case ANALYSIS_TYPES.ATTACHMENTS:
                title = 'Attachment Analysis';
                HeaderIcon = InsertDriveFileIcon;
                break;
            case ANALYSIS_TYPES.EMAILS:
                title = 'Email Address Analysis';
                HeaderIcon = AlternateEmailIcon;
                break;
            case ANALYSIS_TYPES.URLS:
                title = 'URL Analysis';
                HeaderIcon = InsertDriveFileIcon;
                break;
            case ANALYSIS_TYPES.DNS:
                title = 'DNS Analysis';
                HeaderIcon = DnsIcon;
                break;
            case ANALYSIS_TYPES.CONTENT_OVERALL:
                title = 'Text Content';
                HeaderIcon = TextFieldsIcon;
                break;
            case ANALYSIS_TYPES.HEADER_ANALYSIS:
                title = 'Threat Score Rule Applied';
                HeaderIcon = BuildIcon;
                break;
            default:
                break;
        }

        const getEntityValueString = (item: any) =>
          ["sender_email", "sender_domain"].includes(item.entity_type) ? `(${item.value})`
            : ["sender_ip_v4", "sender_ip_v6"].includes(item.entity_type) ? `(${item.value.ip}, location ${item.value.location})` : '';

        const EXCLUDED_SENDERS = new Set(['sender_ip_v4', 'sender_ip_v6']);
        const typeHasValues = () => {
            const data = threatScoreData?.[analysisType];
            if (!data?.length) return false;

            if (analysisType !== ANALYSIS_TYPES.SENDERS) return true;

            return data.some((item: any) => !EXCLUDED_SENDERS.has(item.entity_type));
        };

        return (
          typeHasValues() &&
          <Accordion className={analysisType === ANALYSIS_TYPES.HEADER_ANALYSIS
              ? 'analysis-content threat-score-rule-analysis-content'
              : 'analysis-content'} expanded={analysisExpanded?.[analysisType]}
                     onChange={(event, expanded) => {
                         setAnalysisExpanded?.(prev => ({ ...prev, [analysisType]: expanded }));
                     }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon htmlColor={'#193051'}/>}>
                  <Box className={"entities-header"}>
                      <Box className={"left-entities-header"}>
                          <Box className={"left-icon"}>
                              {analysisType === ANALYSIS_TYPES.URLS ?
                                <Image srcImg={Images.AddLink} width={"24px"} height={"24px"}
                                       cssClass={"add-link-icon"}/> :
                                <HeaderIcon fontSize={"medium"} htmlColor={"#193051"}/>}
                          </Box>
                          <span className={"entities-title"}>{title}</span>
                      </Box>
                      {
                        analysisType !== ANALYSIS_TYPES.CONTENT_OVERALL &&
                        <Tooltip placement={"top"} title={'More Details'}>
                            <Button className={"view-scan-result-detail"}
                                    onClick={() => handleChangeEmailDetailActiveTab(scanResultKey)}>
                                <LaunchIcon fontSize={"medium"} htmlColor={"#193051"}/>
                            </Button>
                        </Tooltip>
                      }
                  </Box>
              </AccordionSummary>
              <AccordionDetails>
                  {
                      analysisType === ANALYSIS_TYPES.SENDERS ? <Box className={"entities-content"}>
                          {
                              threatScoreData.senders.map((item: any, index: any) => {

                                  if (item.value || (item.entity_type !== 'sender_ip_v6' && item.entity_type !== 'sender_ip_v4')) {
                                      let senderMessageIcon = <WarningIcon style={{ marginRight: '8px' }}
                                                                           htmlColor={'#ED1C24'}/>
                                      let senderMessageBox: any;
                                      if (Object.keys(senderObjectNames).includes(item.entity_type)) {
                                          const isHighThreat = item.priority.toLowerCase() === "high"
                                          senderMessageIcon = isHighThreat ?
                                            <WarningIcon style={{ marginRight: '8px' }}
                                                         htmlColor={'#ED1C24'}/> :
                                            <RemoveCircleIcon style={{ marginRight: '8px' }}
                                                              htmlColor={'#FCAB10'}/>
                                          senderMessageBox = <Box>
                                              The <strong>{senderObjectNames[item.entity_type]}</strong> {getEntityValueString(item)} is
                                              classified: <strong>{isHighThreat ? "Threat" : "Undetermined"}</strong>
                                              { !(item.entity_type === "sender_domain") && <Box>Common possibilities (not limited to):</Box> }
                                              {
                                                  item.entity_type === "sender_domain" ? <></> :
                                                      isHighThreat && item.reasons.length > 0 ?
                                                          item.reasons.map((reason: string, idx: any) =>
                                                              <Box sx={{
                                                                  marginLeft: "10px",
                                                                  wordBreak: "break-word",
                                                                  textOverflow: "ellipsis"
                                                              }} key={"reason_" + idx}>. &nbsp; {reason}</Box>
                                                          ) :
                                                          <>
                                                              <Box sx={{
                                                                  marginLeft: "10px",
                                                                  wordBreak: "break-word",
                                                                  textOverflow: "ellipsis"
                                                              }}>. &nbsp;
                                                                  Incomplete or inaccurate geolocation databases
                                                              </Box>
                                                              <Box sx={{
                                                                  marginLeft: "10px",
                                                                  wordBreak: "break-word",
                                                                  textOverflow: "ellipsis"
                                                              }}>. &nbsp;
                                                                  Privacy protection measures
                                                              </Box>
                                                          </>
                                              }
                                          </Box>
                                      } else {
                                          senderMessageBox = <Box style={{ display: "inline-block" }}>
                                              <div><strong>{item.hint}</strong></div>
                                              {item?.content}
                                          </Box>
                                      }

                                      return <Box className={"entities-attribute"} key={index}>
                                          {senderMessageIcon}
                                          <Box className={'entities-attribute-message'}>
                                              {senderMessageBox}
                                          </Box>
                                      </Box>
                                  }
                              })
                          }
                      </Box> : analysisType === ANALYSIS_TYPES.CONTENT_OVERALL ? <Box className={"entities-content"}>
                          {
                              threatScoreData.content_overall.map((item: any, index: any) => {
                                    const textContentMessage: string = `${item.message}`
                                    return <Box className={"entities-attribute"} key={index}>
                                        {messageIcon}
                                        <Box className={'entities-attribute-message'}>{textContentMessage}</Box>
                                    </Box>
                                }
                              )
                          }
                      </Box> : analysisType === ANALYSIS_TYPES.HEADER_ANALYSIS ? <Box className={"entities-content"}>
                          {headerRuleCard && renderThreatScoreRuleCard(headerRuleCard)}
                      </Box> : handleEntitiesContentComponent(scanResultKey, threatScoreData?.[analysisType])
                  }
              </AccordionDetails>
          </Accordion>
        )
    }

    const analysis_list = [
      ANALYSIS_TYPES.SENDERS,
      ANALYSIS_TYPES.IPS,
      ANALYSIS_TYPES.DNS,
      ANALYSIS_TYPES.EMAILS,
      ANALYSIS_TYPES.URLS,
      ANALYSIS_TYPES.ATTACHMENTS,
      ANALYSIS_TYPES.CONTENT_OVERALL,
      ANALYSIS_TYPES.HEADER_ANALYSIS,
    ];

  return (
    isNoData ? <Skeleton variant="rectangular" sx={{height: '100px', borderRadius: '4px'}} /> :
    <Box className={"summary-analysis-viewer-wrapper"}>
        <Box className={"left-partial"}>
            <Box className={"left-partial-header"}>
                <span className={"title"}>Overview</span>
                <ThreatAnalysisTooltipStyle placement={"top"} className={"custom-tooltip"} title={<ThreatScoreTooltip />} >
                    <InfoOutlinedIcon sx={{position: "relative", top: "4px", cursor: "pointer"}} fontSize={"medium"} htmlColor={"#193051"}/>
                </ThreatAnalysisTooltipStyle>
            </Box>
            {
                <Box className={"left-partial-threat-score"}>
                    <Box className={"chart"}>
                        <PieChart
                            series={[
                                {
                                    startAngle: pieChartStartPoint,
                                    endAngle: 90,
                                    paddingAngle: 0,
                                    innerRadius: 100,
                                    outerRadius: 145,
                                    data: [{value: 100, color: '#F2F3F3'}]
                                },
                                {
                                    startAngle: pieChartStartPoint,
                                    endAngle: pieChartEndPoint,
                                    innerRadius: 105,
                                    outerRadius: 140,
                                    arcLabel: getArcLabel,
                                    data: pieChartData,
                                },
                            ]}
                            sx={{
                                [`& .${pieArcLabelClasses.root}`]: {
                                    fill: pieChartData[0]?.color,
                                    textShadow: "0 0 2px rgba(0, 0, 0, 0.25)",
                                    fontFamily: "Montserrat",
                                    fontSize: "45px",
                                    fontWeight: 700,
                                    transform: "translate(0, -20%)!important"
                                },
                                [`& .${pieArcClasses.root}`]: {
                                    stroke: '#E0E0E0'
                                }
                            }}
                            skipAnimation
                            tooltip={{ trigger: 'none' }}
                            slotProps={{legend: { hidden: true },}}
                            margin={{right: 0, left: 0, top: 145}}
                        />
                    </Box>
                    <Box className={"chart-info"}>
                        <span >0</span>
                        <span >Threat Score</span>
                        <span >100</span>
                    </Box>
                    <Box className={"result-score"}>
                        <span style={{backgroundColor: pieChartData[0]?.color}}>{pieChartData[0]?.label}</span>
                    </Box>
                    <Box className={"result-score-explain"}>
                        {explainText}
                    </Box>
                </Box>
            }
            <Box className={"left-partial-result"}>
                <Box className={"priority-info"} >
                    <Box>
                        <span style={{fontWeight: 500}}>Priority: </span>
                    </Box>
                    <Box className={"priority-value"} style={priorityStyleData[0]?.style}>{priorityStyleData[0]?.text}</Box>
                </Box>
                <Box className={"category-info"}>
                    <Box>
                        <span style={{fontWeight: 500}}>Category: </span>
                    </Box>
                    <Box style={{fontWeight: 600}}>{summary_analysis_category}</Box>
                </Box>
            </Box>
            <Box className={"left-partial-description"}>
                <span style={{color: "#00000099", fontSize: "16px", fontWeight: 600}}>What should we do?</span>
                <Box className={'description-box'} sx={{backgroundColor: "#EDF1F5"}} >
                    <Box sx={{width: "4px", backgroundColor: "#467599", borderRadius: "4px 0 0 4px"}} />
                    <Box sx={{margin: "8px", display: "flex", alignItems: "center"}}>
                        <Box className={'info-icon'}>
                            <ErrorIcon fontSize={"small"} sx={{display: "flex", height: "auto"}}/>
                        </Box>
                        <Box className={'description-content'} sx={{color: "#467599"}}>
                            {priorityStyleData[0]?.suggestionContent}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>

        <Box className={"right-partial"}>
            <span className={"title"}>Key indicator</span>
            {
                !analysis_list.some(item => threatScoreData?.[item]?.length > 0) ? <NoThreatDetectedCard score={threatScoreData.score}/> :
                <Fragment>
                    {handleReasonNote()}
                    {analysis_list.map(item => getAnalysis(item))}
                </Fragment>
            }
        </Box>
    </Box>
  );
};
